import React, { FC, useEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import {
    HexOrNamedColor,
    Button,
    ComponentTextSmall,
    Icon,
    pxToRem,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { usePopper } from 'react-popper';
import { Link } from 'react-router-dom';

import { DropdownMenuTestID } from 'src/shared/components/testIds';

export interface DropdownMenuItemProps {
    title: string;
    linkTo: string;
    color?: HexOrNamedColor;
}

export const DropdownMenuItem = React.forwardRef<HTMLAnchorElement, DropdownMenuItemProps>(
    ({ title, linkTo, color = 'gray65' }, ref) => {
        return (
            <ItemContainer to={linkTo} innerRef={ref}>
                <ComponentTextSmall fontColor={color}>{title}</ComponentTextSmall>
            </ItemContainer>
        );
    },
);

interface DropdownMenuProps {
    buttonTitle?: string;
    popperAriaLabel?: string;
}

export const DropdownMenu: FC<DropdownMenuProps> = ({ buttonTitle, children, popperAriaLabel }) => {
    const [popperOpen, setPopperOpen] = useState(false);
    const popperElRef = useRef<HTMLDivElement>(null);
    const referenceElRef = useRef<HTMLButtonElement>(null);
    const arrowElRef = useRef<HTMLDivElement>(null);
    const { update: popperUpdate, ...popper } = usePopper(
        referenceElRef.current,
        popperElRef.current,
        {
            placement: 'bottom-end',
            modifiers: [
                {
                    name: 'offset',
                    options: {
                        offset: [0, 10],
                    },
                },
                { name: 'arrow', options: { element: arrowElRef.current } },
            ],
        },
    );

    useEffect(() => {
        if (popperOpen) {
            popperElRef.current?.setAttribute('open', '');
        } else {
            popperElRef.current?.removeAttribute('open');
        }
        popperUpdate?.();
    }, [popperUpdate, popperOpen]);

    return (
        <>
            <Button
                title={buttonTitle}
                variant="secondary"
                onClick={() => setPopperOpen(val => !val)}
                ref={referenceElRef}
                aria-expanded={popperOpen}
                size="small"
                css={{ padding: pxToRem(10) }}
                data-testid={DropdownMenuTestID.MenuButton}
            >
                <Icon name="more" aria-hidden="true" size={16} />
            </Button>
            <Popper
                aria-label={popperAriaLabel}
                ref={popperElRef}
                style={popper.styles.popper}
                {...popper.attributes}
            >
                <PopperArrow ref={arrowElRef} style={popper.styles.arrow} />
                <ItemsContainer onClick={() => setPopperOpen(false)}>{children}</ItemsContainer>
            </Popper>
        </>
    );
};

const ItemsContainer = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
});

const Popper = styled.dialog({
    display: 'none',
    backgroundColor: 'white',
    borderRadius: pxToRem(6),
    border: `${pxToRem(2)} solid ${colors.gray05}`,
    minWidth: pxToRem(200),
    maxWidth: pxToRem(450),
    boxShadow: `${pxToRem(5, 5, 5)} rgba(193, 198, 211, 0.2)`,
    '&[open]': {
        display: 'block',
    },
    // Display over page elements, still allowing for an index to be
    // used by page elements below this, while still keeping this
    // below modals.
    zIndex: 2,
});

const PopperArrow = styled.div({
    top: pxToRem(-9),

    '&, &::before': {
        position: 'absolute',
        width: pxToRem(16),
        height: pxToRem(16),
        zIndex: 1,
    },

    '&::before': {
        content: '""',
        transform: 'rotate(45deg)',
        backgroundColor: 'white',
        border: `${pxToRem(2)} solid ${colors.gray05}`,
        borderRightWidth: 0,
        borderBottomWidth: 0,
    },
});

const ItemContainer = styled(Link)({
    color: 'unset',
    padding: pxToRem(9, 0),
});
